
import React, { useState, useEffect } from 'react';
import { Clock, ChevronRight } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { t } from '../../i18n';

const LOCAL_SELECTION_KEY = 'xleven_league_session';
const UNKNOWN_SESSION_TTL = 1 * 60 * 1000;

export const GuestTimerBanner: React.FC = () => {
    const { lang } = useLanguage();
    const [timeLeft, setTimeLeft] = useState<number | null>(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const checkSession = () => {
            const sessionStr = localStorage.getItem(LOCAL_SELECTION_KEY);
            if (!sessionStr) {
                setIsVisible(false);
                return;
            }

            const session = JSON.parse(sessionStr);
            const now = Date.now();
            const elapsed = now - session.timestamp;
            const remaining = UNKNOWN_SESSION_TTL - elapsed;

            if (remaining <= 0) {
                localStorage.removeItem(LOCAL_SELECTION_KEY);
                if (window.navigateTo) window.navigateTo('/login');
                setIsVisible(false);
            } else {
                setTimeLeft(Math.floor(remaining / 1000));
                setIsVisible(true);
            }
        };

        checkSession();
        const timer = setInterval(checkSession, 1000);
        return () => clearInterval(timer);
    }, []);

    if (!isVisible || timeLeft === null) return null;

    // Check if in capture mode via URL
    const isCaptureMode = window.location.search.includes('capture=true') || window.location.hash.includes('capture=true');
    if (isCaptureMode) return null;

    const minutes = Math.floor(timeLeft / 60);
    const seconds = timeLeft % 60;
    const timeStr = `${minutes}:${seconds.toString().padStart(2, '0')}`;

    const handleBannerClick = () => {
        if (window.navigateTo) window.navigateTo('/login');
    };

    return (
        <div 
            className="fixed bottom-[75px] md:bottom-8 left-0 right-0 z-[100] flex justify-center px-4 pointer-events-none animate-in slide-in-from-bottom-4 duration-500 print:hidden capture-ignore" 
            id="guest-timer-banner"
            style={{ isolation: 'isolate' }}
        >
            <div className="w-full max-w-[1200px] flex justify-center relative md:pl-20 lg:pl-64 md:pr-80">
                <button 
                    onClick={handleBannerClick}
                    className="pointer-events-auto w-full md:max-w-[440px] bg-white/90 backdrop-blur-xl border border-gray-100 rounded-[32px] shadow-[0_20px_50px_rgba(0,0,0,0.12)] p-1.5 flex items-center gap-4 group transition-all md:hover:scale-[1.02] active:scale-95 ring-1 ring-black/5"
                >
                    <div className="w-14 h-14 md:w-16 md:h-16 rounded-[24px] bg-slate-50 overflow-hidden shrink-0 flex items-center justify-center p-2.5 relative">
                        <div className="relative z-10">
                            <Clock size={28} className="text-rose-500 animate-pulse" />
                        </div>
                        <div className="absolute inset-0 bg-rose-500/5 rounded-full animate-ping opacity-20" />
                    </div>

                    <div className="flex-1 text-left min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                            <span className="bg-rose-500 text-white text-[8px] font-black px-1.5 py-0.5 rounded-md uppercase tracking-widest shadow-sm shadow-rose-100">GUEST</span>
                            <span className="text-rose-600 font-black text-xs md:text-sm tabular-nums tracking-tighter">{timeStr}</span>
                        </div>
                        <h3 className="text-slate-900 font-black text-[13px] md:text-sm tracking-tight truncate uppercase">
                            {t(lang, 'unlockFullIntel')}
                        </h3>
                    </div>

                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-slate-300 md:group-hover:text-rose-500 md:group-hover:bg-rose-50 transition-all mr-1 shrink-0">
                        <ChevronRight size={20} strokeWidth={3} />
                    </div>
                </button>
            </div>
        </div>
    );
};
